/**
 * Custom hook for bill file search using Elasticsearch
 * 
 * Supports filtering, pagination, and full-text search
 */
import { useQuery } from '@tanstack/react-query'
import {
  searchBillFiles,
  type BillFileSearchFilters,
  type BillFileSearchResponse,
} from '../api/billFileSearchApi'

/**
 * Hook for searching bill files with Elasticsearch
 * 
 * @param filters - Search filters
 * @param options - React Query options
 * @returns Query result with bill file data
 */
export function useBillFileSearch(
  filters: BillFileSearchFilters | null,
  options: {
    enabled?: boolean
    staleTime?: number
  } = {}
) {
  const safeFilters: BillFileSearchFilters = filters ?? { q: '' }
  const { enabled = true, staleTime = 30000 } = options

  const query = useQuery<BillFileSearchResponse, Error>({
    queryKey: ['bill-file-search', safeFilters],
    queryFn: () => searchBillFiles(safeFilters),
    enabled: enabled && !!safeFilters.q && safeFilters.q.trim().length > 0,
    staleTime,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    ...query,
    files: query.data?.results ?? [],
    total: query.data?.total ?? 0,
    maxScore: query.data?.max_score,
    hasMore: query.data?.page?.has_more ?? false,
    currentPage: Math.floor((safeFilters.from ?? 0) / (safeFilters.size ?? 20)),
  }
}

// Re-export types
export type { BillFileSearchFilters, BillFileSearchResponse }
